// Marker icons that clients can choose from for the operator map.
// Colors are NOT chosen by users — color reflects status (online / background / offline).
//
// 'initials' is a special mode: instead of an SVG glyph, the marker shows up to
// two uppercase letters derived from the user's full name. svgPath is empty for
// this entry and consumers must special-case `key === 'initials'`.

export type MarkerIconKey =
  | 'initials'
  | 'person'
  | 'home'
  | 'building'
  | 'briefcase'
  | 'shield'
  | 'star'
  | 'heart'
  | 'car'
  | 'bolt'
  | 'crown'
  | 'diamond'
  | 'key'
  | 'paw'
  | 'flag'
  | 'flower'
  | 'tools'
  | 'motorcycle'
  | 'badge';

/**
 * SVG path data sized for a 24x24 viewBox. Drawn in white inside the colored
 * status circle. The web map composes the final marker; the mobile picker
 * shows the emoji as a lightweight preview.
 *
 * The 'initials' entry has an empty svgPath — consumers render text instead.
 */
export const MARKER_ICONS: Record<
  MarkerIconKey,
  { label: string; emoji: string; svgPath: string }
> = {
  initials: {
    label: 'Iniciales',
    emoji: 'Aa',
    svgPath: '',
  },
  person: {
    label: 'Persona',
    emoji: '👤',
    svgPath:
      'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM4 22c0-4.42 3.58-8 8-8s8 3.58 8 8z',
  },
  home: {
    label: 'Casa',
    emoji: '🏠',
    svgPath: 'M12 3L3 12h2v8h5v-5h4v5h5v-8h2z',
  },
  building: {
    label: 'Edificio',
    emoji: '🏢',
    svgPath:
      'M5 3h14v18H5zM8 6h2v2H8zM14 6h2v2h-2zM8 10h2v2H8zM14 10h2v2h-2zM8 14h2v2H8zM14 14h2v2h-2zM10 18h4v3h-4z',
  },
  briefcase: {
    label: 'Oficina',
    emoji: '💼',
    svgPath: 'M9 3h6v3h4v15H5V6h4zM11 5v1h2V5z',
  },
  shield: {
    label: 'VIP',
    emoji: '🛡️',
    svgPath: 'M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5z',
  },
  star: {
    label: 'Favorito',
    emoji: '⭐',
    svgPath:
      'M12 2l3 7h7l-5.5 4.5L18.5 22 12 17l-6.5 5L7.5 13.5 2 9h7z',
  },
  heart: {
    label: 'Familia',
    emoji: '❤️',
    svgPath:
      'M12 21s-7-4.5-9.5-9c-1-1.8-1.5-4.4.5-6.5 2-2 5-1.5 6.5.5L12 7l2.5-1c1.5-2 4.5-2.5 6.5-.5 2 2.1 1.5 4.7.5 6.5-2.5 4.5-9.5 9-9.5 9z',
  },
  car: {
    label: 'Vehículo',
    emoji: '🚗',
    svgPath:
      'M5 11l2-5h10l2 5v6h-2v-2H7v2H5zM7 14a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0M14 14a1.5 1.5 0 1 0 3 0 1.5 1.5 0 1 0-3 0',
  },
  bolt: {
    label: 'Rayo',
    emoji: '⚡',
    svgPath: 'M13 2L4 14h7l-1 8 9-12h-7z',
  },
  crown: {
    label: 'Corona',
    emoji: '👑',
    svgPath: 'M3 8l4 4 5-7 5 7 4-4-2 11H5z',
  },
  diamond: {
    label: 'Diamante',
    emoji: '💎',
    svgPath: 'M6 3h12l4 6-10 12L2 9z M9 9h6 M6 3l3 6 M18 3l-3 6',
  },
  key: {
    label: 'Llave',
    emoji: '🔑',
    svgPath:
      'M14 2a6 6 0 1 0-5.66 8H10v3h2v3h3v-3l3-3a6 6 0 0 0-4-8zM14 8a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z',
  },
  paw: {
    label: 'Mascota',
    emoji: '🐾',
    svgPath:
      'M5 13a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM19 13a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM9 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM15 7a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM12 14c-3 0-6 3-6 5a2 2 0 0 0 2 2c1 0 2-1 4-1s3 1 4 1a2 2 0 0 0 2-2c0-2-3-5-6-5z',
  },
  flag: {
    label: 'Bandera',
    emoji: '🚩',
    svgPath: 'M5 3v18h2v-7h11l-3-4 3-4H7V3z',
  },
  flower: {
    label: 'Jardín',
    emoji: '🌸',
    svgPath:
      'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM12 8c1.5-3 6-3 6 0s-4.5 3-6 0zM12 16c1.5 3 6 3 6 0s-4.5-3-6 0zM12 8c-1.5-3-6-3-6 0s4.5 3 6 0zM12 16c-1.5 3-6 3-6 0s4.5-3 6 0z',
  },
  tools: {
    label: 'Taller',
    emoji: '🔧',
    svgPath:
      'M14 2a5 5 0 0 0-4.9 6L3 14l4 4 6-6.1A5 5 0 1 0 14 2zm0 8a3 3 0 1 1 0-6 3 3 0 0 1 0 6z',
  },
  motorcycle: {
    label: 'Motocicleta',
    emoji: '🏍️',
    svgPath:
      'M5 14a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm14 0a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM5 17h14M7 17l4-6h4l3 6M13 5h4v3l-3 3',
  },
  badge: {
    label: 'Patrullero',
    emoji: '🛡️',
    svgPath:
      'M12 2l8 3v6c0 5-3.5 9.5-8 11-4.5-1.5-8-6-8-11V5zM9 11l2 2 4-4',
  },
};

export const MARKER_ICON_KEYS = Object.keys(MARKER_ICONS) as MarkerIconKey[];

export function isMarkerIconKey(value: unknown): value is MarkerIconKey {
  return typeof value === 'string' && value in MARKER_ICONS;
}

/**
 * Derive up to 2 uppercase letters from a person's full name for the
 * 'initials' marker mode. Falls back to '?' when name is empty.
 */
export function initialsFromName(fullName: string | null | undefined): string {
  const name = (fullName ?? '').trim();
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}
